import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  ChevronLeft, ChevronRight, CheckCircle2, Circle, PlayCircle, Clock, Lock,
  Paperclip, Download,
} from "lucide-react";
import {
  getLessonBlocks, vimeoEmbedUrl, groupLessonsByHierarchy, flattenLessons,
} from "@/lib/courseUtils";

// Normalize image URLs — pass through absolute http(s) URLs, prefix relative
// ones with the backend origin.
function backendUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

/**
 * Member-facing course viewer.
 *
 * Layout (as requested by user):
 *  ┌────────────────────────────────────────┐
 *  │ Current lesson (video + text blocks)   │
 *  │ ...                                    │
 *  │ [Previous]  [Next]  [Complete lesson]  │  ← buttons inside the frame
 *  └────────────────────────────────────────┘
 *  ┌────────────────────────────────────────┐
 *  │ Course progress %                      │  ← separate frame
 *  └────────────────────────────────────────┘
 *  ┌────────────────────────────────────────┐
 *  │ All course lessons (clickable list)    │  ← below, no side sidebar
 *  │ ...                                    │
 *  └────────────────────────────────────────┘
 *
 * Theme is chosen by the ADMIN in /admin/courses (Apple / MasterClass / Notion).
 * Members do not see a theme switcher. Apple is the default. The three themes
 * only change color/typography — the structural layout stays identical.
 */
export default function CourseDetailPage() {
  const { courseId } = useParams();
  const [data, setData] = useState(null);
  const [activeLessonId, setActiveLessonId] = useState(null);

  const load = useCallback(
    () => api.get(`/courses/${courseId}`).then((r) => setData(r.data)),
    [courseId]
  );
  useEffect(() => { load().catch((e) => toast.error(extractError(e))); }, [load]);

  const hierarchy = useMemo(
    () => (data ? groupLessonsByHierarchy(data.course, data.lessons) : []),
    [data]
  );
  const flatOrdered = useMemo(() => flattenLessons(hierarchy), [hierarchy]);

  useEffect(() => {
    if (!activeLessonId && flatOrdered.length) {
      const last = data?.enrollment?.last_lesson_id;
      const start = flatOrdered.find((l) => l.id === last)?.id || flatOrdered[0].id;
      setActiveLessonId(start);
    }
  }, [flatOrdered, activeLessonId, data]);

  useEffect(() => {
    if (activeLessonId && courseId) {
      api.post(`/courses/${courseId}/lessons/${activeLessonId}/view`).catch(() => {});
    }
  }, [activeLessonId, courseId]);

  const activeIdx = flatOrdered.findIndex((l) => l.id === activeLessonId);
  const active = activeIdx >= 0 ? flatOrdered[activeIdx] : null;
  const prev = activeIdx > 0 ? flatOrdered[activeIdx - 1] : null;
  const next = activeIdx >= 0 && activeIdx < flatOrdered.length - 1 ? flatOrdered[activeIdx + 1] : null;
  const displayIdx = activeIdx >= 0 ? activeIdx : 0;

  const complete = async () => {
    try {
      const r = await api.post(`/courses/${courseId}/lessons/${activeLessonId}/complete`);
      toast.success(r.data.preview ? "معاينة الأدمن — تم" : `تم! تقدّمك: ${r.data.progress_pct}%`);
      await load();
      if (next) {
        setActiveLessonId(next.id);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (e) { toast.error(extractError(e)); }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  if (!data) return <div className="p-8 text-center text-foreground/50">جاري تحميل الدورة...</div>;

  const theme = data.course.theme || "apple";
  const progressPct = data.enrollment?.progress_pct || 0;

  // Theme classes for the frames
  const styles = getThemeStyles(theme);

  return (
    <div dir="rtl" className={`space-y-5 ${styles.pageBg}`} data-testid={`course-viewer-${theme}`}>
      {/* Course title header (no back button per user request) */}
      <div className="space-y-1">
        <h1 className={styles.courseTitle} data-testid="course-title">{data.course.title}</h1>
        {data.course.subtitle && (
          <p className={styles.courseSubtitle}>{data.course.subtitle}</p>
        )}
      </div>

      {/* ═════ Current lesson frame ═════ */}
      <Card className={`${styles.frame} overflow-hidden`} data-testid="current-lesson-frame">
        <div className={styles.framePadding}>
          <div className="space-y-2">
            <div className={styles.chapterLabel}>
              الدرس {displayIdx + 1} من {flatOrdered.length}
            </div>
            <h2 className={styles.lessonTitle} data-testid="active-lesson-title">
              {active?.title || "لم يتم اختيار درس"}
            </h2>
            {active?.duration_min ? (
              <div className={styles.metaLine}>
                <Clock className="h-3.5 w-3.5" /> {active.duration_min} دقيقة
              </div>
            ) : null}
          </div>

          {/* Blocks (video + text) rendered in order */}
          <div className="mt-6 space-y-6" data-testid="lesson-blocks">
            {active && getLessonBlocks(active).map((b) => (
              <LessonBlockRenderer key={b.id} block={b} theme={theme} />
            ))}
            {active && getLessonBlocks(active).length === 0 && (
              <div className={styles.emptyContent}>لا يوجد محتوى في هذا الدرس بعد.</div>
            )}
          </div>

          {/* Downloadable attachments — PDFs, worksheets, meal plans, etc.
              Rendered under the lesson content, always download-able (never
              inline previewed) so users get a clear "save file" affordance. */}
          {active?.attachments?.length > 0 && (
            <div className="mt-8" data-testid="lesson-attachments">
              <div className="flex items-center gap-2 mb-3 font-semibold text-foreground/80">
                <Paperclip className="h-4 w-4" />
                <span>ملفات مرفقة</span>
              </div>
              <ul className="space-y-2">
                {active.attachments.map((a, i) => (
                  <li key={i}>
                    <a
                      href={a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={a.name || undefined}
                      className="flex items-center justify-between gap-3 rounded-xl border border-foreground/10 bg-card px-4 py-3 hover:border-primary/50 hover:bg-primary/[0.03] transition"
                      data-testid={`lesson-attachment-${i}`}
                    >
                      <span className="flex items-center gap-2 min-w-0 flex-1">
                        <Paperclip className="h-4 w-4 shrink-0 text-primary" />
                        <span className="truncate text-sm">{a.name || "ملف"}</span>
                      </span>
                      <Download className="h-4 w-4 shrink-0 text-foreground/50" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Prev / Next / Complete — inside the frame per user request */}
          <div className="mt-8 pt-5 border-t border-foreground/5 flex items-center justify-between flex-wrap gap-3" data-testid="lesson-nav-bar">
            <button
              onClick={() => { if (prev) { setActiveLessonId(prev.id); scrollToTop(); } }}
              disabled={!prev}
              className={`${styles.btnGhost} disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1`}
              data-testid="lesson-prev"
            >
              <ChevronRight className="h-4 w-4" /> السابق
            </button>

            <div className="flex items-center gap-2 flex-wrap">
              {active && !active.completed && (
                <button onClick={complete} className={styles.btnComplete} data-testid="lesson-complete">
                  <CheckCircle2 className="h-4 w-4 me-1 inline" />إتمام الدرس
                </button>
              )}
              {active?.completed && (
                <span className={styles.completedTag}>
                  <CheckCircle2 className="h-4 w-4 me-1 inline" />تم إتمام هذا الدرس
                </span>
              )}
              <button
                onClick={() => { if (next) { setActiveLessonId(next.id); scrollToTop(); } }}
                disabled={!next}
                className={`${styles.btnPrimary} disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1`}
                data-testid="lesson-next"
              >
                التالي <ChevronLeft className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* ═════ Progress frame ═════ */}
      <Card className={styles.frame} data-testid="progress-frame">
        <div className={styles.framePadding}>
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div>
              <div className={styles.progressLabel}>تقدّمك في الدورة</div>
              <div className={styles.progressValue}>{progressPct}%</div>
            </div>
            <div className="text-sm opacity-70 tabular-nums">
              {flatOrdered.filter((l) => l.completed).length} / {flatOrdered.length} درس
            </div>
          </div>
          <Progress value={progressPct} className={styles.progressBar} />
        </div>
      </Card>

      {/* ═════ All lessons list ═════ */}
      <Card className={styles.frame} data-testid="all-lessons-frame">
        <div className={styles.framePadding}>
          <h3 className={styles.sectionTitle}>جميع دروس الدورة</h3>
          <div className="mt-4 space-y-5">
            {hierarchy.map((sec) => (
              <div key={sec.id} className="space-y-2">
                <div className={styles.sectionLabel}>{sec.title || "بدون قسم"}</div>
                {sec.lessons.map((l) => (
                  <LessonRow
                    key={l.id} l={l} isActive={l.id === activeLessonId}
                    onClick={() => { setActiveLessonId(l.id); scrollToTop(); }}
                    styles={styles}
                  />
                ))}
                {sec.subsections.map((sub) => (
                  <div key={sub.id} className="ps-4 border-s-2 border-orange-100 space-y-1.5 pt-1">
                    <div className={styles.subSectionLabel}>{sub.title}</div>
                    {sub.lessons.map((l) => (
                      <LessonRow
                        key={l.id} l={l} isActive={l.id === activeLessonId}
                        onClick={() => { setActiveLessonId(l.id); scrollToTop(); }}
                        styles={styles}
                      />
                    ))}
                  </div>
                ))}
              </div>
            ))}
            {hierarchy.length === 0 && (
              <div className="text-sm text-foreground/50 py-6 text-center">لا توجد دروس في هذه الدورة بعد.</div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cloudflare Stream player.
//
// - On iOS (Safari + iOS PWA): renders a native <video> element pointed at
//   the signed HLS manifest. iOS Safari plays HLS natively, and its native
//   fullscreen button auto-rotates to landscape exactly like YouTube.
// - Everywhere else: renders the signed CF Stream iframe (nicer UI: chapters,
//   quality selector, thumbnails). On fullscreen we also lock landscape via
//   the Screen Orientation API (works on Android Chrome / Firefox / Samsung
//   and Android PWA; iOS never reaches this branch).
//
// Any authenticated user can request a token — the entire app is gated at
// login, so no per-course entitlement check is performed.
// ---------------------------------------------------------------------------
const IS_IOS =
  typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS 13+ reports itself as MacIntel but has touch — treat as iOS.
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

function CloudflareStreamPlayer({ uid, fallbackIframeUrl, title }) {
  const [iframeSrc, setIframeSrc] = useState("");
  const [hlsSrc, setHlsSrc] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setIframeSrc(""); setHlsSrc(""); setError("");
    if (!uid) return;
    (async () => {
      try {
        const { data } = await api.post("/stream/playback-token", { uid });
        if (cancelled) return;
        setIframeSrc(data?.iframe_src || "");
        setHlsSrc(data?.hls_src || "");
      } catch (e) {
        if (cancelled) return;
        if (fallbackIframeUrl) setIframeSrc(fallbackIframeUrl);
        else setError(extractError(e));
      }
    })();
    return () => { cancelled = true; };
  }, [uid, fallbackIframeUrl]);

  // Non-iOS: force landscape on fullscreen (silently ignored on Safari).
  useEffect(() => {
    if (IS_IOS) return;
    const onFsChange = async () => {
      const inFs = !!(document.fullscreenElement || document.webkitFullscreenElement);
      try {
        if (inFs && window.screen?.orientation?.lock) {
          await window.screen.orientation.lock("landscape");
        } else if (!inFs && window.screen?.orientation?.unlock) {
          window.screen.orientation.unlock();
        }
      } catch (_) { /* older Androids — silently ignore */ }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);

  if (error) {
    return <div className="w-full h-full bg-foreground/5 grid place-items-center text-foreground/40 text-sm px-4 text-center">{error}</div>;
  }
  if (IS_IOS ? !hlsSrc : !iframeSrc) {
    return <div className="w-full h-full bg-foreground/5 grid place-items-center text-foreground/40 text-sm">جاري تحميل الفيديو…</div>;
  }
  if (IS_IOS) {
    return (
      <video
        src={hlsSrc}
        title={title || "Video"}
        controls
        playsInline
        className="w-full h-full bg-black"
      />
    );
  }
  return (
    <iframe
      src={iframeSrc}
      title={title || "Video"}
      className="w-full h-full"
      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
      allowFullScreen
    />
  );
}

// ---------------------------------------------------------------------------
// Renders a single content block (video / text)
// ---------------------------------------------------------------------------
function LessonBlockRenderer({ block, theme }) {
  if (block.type === "video") {
    const isCfStream = block.video_source === "cf_stream" && block.stream_uid;
    const src = block.video_source === "vimeo"
      ? vimeoEmbedUrl(block.vimeo_id || block.upload_url)
      : block.upload_url;
    return (
      <div className="space-y-2" data-testid={`block-video-${block.id}`}>
        {block.video_title && (
          <h3 className={
            theme === "masterclass" ? "text-white text-xl font-semibold" :
            theme === "notion" ? "text-gray-900 text-lg font-semibold" :
            "text-gray-900 text-xl font-light tracking-tight"
          }>{block.video_title}</h3>
        )}
        <div className={`aspect-video overflow-hidden ${
          theme === "masterclass" ? "rounded-none ring-1 ring-white/10" :
          theme === "notion" ? "rounded-md border border-gray-200" :
          "rounded-2xl shadow-xl shadow-orange-500/10"
        }`}>
          {isCfStream ? (
            <CloudflareStreamPlayer uid={block.stream_uid} fallbackIframeUrl={block.stream_iframe_url} title={block.video_title} />
          ) : src ? (
            block.video_source === "vimeo" ? (
              <iframe
                src={src} title={block.video_title || "Video"}
                className="w-full h-full" allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video src={src} controls className="w-full h-full bg-black" />
            )
          ) : (
            <div className="w-full h-full bg-foreground/5 grid place-items-center text-foreground/40 text-sm">لا يوجد فيديو</div>
          )}
        </div>
        {block.caption && (
          <p className={
            theme === "masterclass" ? "text-white/70 text-sm leading-loose" :
            theme === "notion" ? "text-gray-600 text-sm leading-relaxed" :
            "text-gray-500 text-sm leading-loose"
          }>{block.caption}</p>
        )}
      </div>
    );
  }
  if (block.type === "image") {
    return (
      <figure className="space-y-2" data-testid={`block-image-${block.id}`}>
        <div className={`overflow-hidden ${
          theme === "masterclass" ? "rounded-none ring-1 ring-white/10" :
          theme === "notion" ? "rounded-md border border-gray-200" :
          "rounded-2xl shadow-lg shadow-orange-500/10"
        }`}>
          <img
            src={backendUrl(block.image_url)}
            alt={block.image_alt || ""}
            className="w-full h-auto block"
            loading="lazy"
          />
        </div>
        {block.caption && (
          <figcaption className={
            theme === "masterclass" ? "text-white/70 text-sm leading-loose text-center" :
            theme === "notion" ? "text-gray-600 text-sm leading-relaxed text-center" :
            "text-gray-500 text-sm leading-loose text-center"
          }>{block.caption}</figcaption>
        )}
      </figure>
    );
  }
  if (block.type === "text") {
    return (
      <div
        className={
          theme === "masterclass" ? "prose prose-invert prose-lg max-w-none text-white/85" :
          theme === "notion" ? "prose max-w-none text-gray-800" :
          "prose prose-lg max-w-none text-gray-800"
        }
        data-testid={`block-text-${block.id}`}
        dangerouslySetInnerHTML={{ __html: block.content || "" }}
      />
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// One row in the "all lessons" list
// ---------------------------------------------------------------------------
function LessonRow({ l, isActive, onClick, styles }) {
  const Icon = l.completed ? CheckCircle2 : (isActive ? PlayCircle : Circle);
  return (
    <button
      onClick={onClick}
      className={isActive ? styles.lessonRowActive : styles.lessonRow}
      data-testid={`lesson-row-${l.id}`}
    >
      <Icon className={`h-4 w-4 shrink-0 ${l.completed ? "text-orange-500" : isActive ? "text-orange-500" : "opacity-40"}`} />
      <span className="flex-1 truncate text-start">{l.title}</span>
      {l.duration_min ? (
        <span className="text-[10px] opacity-60 shrink-0">{l.duration_min}د</span>
      ) : null}
      {!l.can_view && l.is_locked ? <Lock className="h-3 w-3 opacity-40" /> : null}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Theme styles — Apple is the primary. MasterClass & Notion inherit the same
// structural layout with different color/typography accents.
// ---------------------------------------------------------------------------
function getThemeStyles(theme) {
  if (theme === "masterclass") {
    return {
      pageBg: "",
      courseTitle: "text-2xl sm:text-3xl font-serif tracking-tight text-white",
      courseSubtitle: "text-sm text-white/60",
      frame: "bg-neutral-900 border border-white/10 text-white shadow-xl",
      framePadding: "p-5 sm:p-7",
      chapterLabel: "text-[10px] uppercase tracking-[0.35em] text-orange-500",
      lessonTitle: "text-2xl sm:text-3xl font-serif text-white tracking-tight",
      metaLine: "text-xs text-white/60 flex items-center gap-1",
      emptyContent: "text-center py-10 text-white/40 text-sm",
      btnGhost: "text-white/70 hover:text-white text-sm",
      btnPrimary: "bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-none uppercase tracking-widest text-xs font-medium",
      btnComplete: "bg-white text-foreground hover:bg-white/90 px-5 py-2.5 rounded-none uppercase tracking-widest text-xs font-medium",
      completedTag: "text-xs uppercase tracking-widest text-orange-400",
      progressLabel: "text-[10px] uppercase tracking-[0.35em] text-orange-500",
      progressValue: "text-3xl font-serif text-white",
      progressBar: "h-1.5 bg-white/10",
      sectionTitle: "text-lg font-serif text-white",
      sectionLabel: "text-[10px] uppercase tracking-[0.3em] text-orange-500",
      subSectionLabel: "text-xs text-white/50",
      lessonRow: "w-full text-start px-3 py-2.5 text-sm flex items-center gap-3 rounded-none hover:bg-white/5 text-white/80 transition-colors",
      lessonRowActive: "w-full text-start px-3 py-2.5 text-sm flex items-center gap-3 rounded-none bg-orange-500/15 text-orange-300 border-r-2 border-orange-500 transition-colors",
    };
  }
  if (theme === "notion") {
    return {
      pageBg: "",
      courseTitle: "text-2xl sm:text-3xl font-semibold text-foreground",
      courseSubtitle: "text-sm text-muted-foreground",
      frame: "bg-white border border-gray-200 shadow-sm",
      framePadding: "p-5 sm:p-6",
      chapterLabel: "text-xs text-gray-400 uppercase tracking-wider font-medium",
      lessonTitle: "text-2xl font-semibold text-gray-900",
      metaLine: "text-xs text-gray-500 flex items-center gap-1",
      emptyContent: "text-center py-10 text-gray-400 text-sm",
      btnGhost: "text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md hover:bg-gray-100 text-sm",
      btnPrimary: "bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md text-sm font-medium",
      btnComplete: "bg-gray-900 text-white hover:bg-gray-800 px-4 py-2 rounded-md text-sm font-medium",
      completedTag: "text-xs text-orange-600 font-medium",
      progressLabel: "text-xs text-gray-500 uppercase tracking-wider font-medium",
      progressValue: "text-2xl font-semibold text-gray-900",
      progressBar: "h-2",
      sectionTitle: "text-base font-semibold text-gray-900",
      sectionLabel: "text-xs text-gray-500 font-medium uppercase tracking-wider",
      subSectionLabel: "text-xs text-gray-400",
      lessonRow: "w-full text-start px-3 py-2 text-sm flex items-center gap-2 rounded hover:bg-gray-100 text-gray-700 transition-colors",
      lessonRowActive: "w-full text-start px-3 py-2 text-sm flex items-center gap-2 rounded bg-orange-50 text-orange-800 font-medium transition-colors",
    };
  }
  // apple (default, primary)
  return {
    pageBg: "",
    courseTitle: "text-3xl sm:text-4xl font-light tracking-tight text-foreground",
    courseSubtitle: "text-sm text-muted-foreground",
    frame: "bg-white/80 backdrop-blur-xl border border-orange-100 shadow-[0_4px_30px_rgba(251,146,60,0.08)] rounded-2xl",
    framePadding: "p-5 sm:p-8",
    chapterLabel: "text-xs uppercase tracking-[0.25em] text-orange-600 font-medium",
    lessonTitle: "text-3xl sm:text-4xl font-light tracking-tight text-gray-900",
    metaLine: "text-sm text-gray-500 flex items-center gap-1",
    emptyContent: "text-center py-12 text-gray-400 text-sm",
    btnGhost: "text-gray-600 hover:text-orange-700 px-3 py-2 rounded-full hover:bg-orange-50 text-sm transition-colors",
    btnPrimary: "bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-5 py-2.5 rounded-full shadow-lg shadow-orange-500/25 text-sm font-medium transition-all",
    btnComplete: "bg-gradient-to-l from-gray-900 to-black text-white px-5 py-2.5 rounded-full text-sm font-medium shadow-lg hover:opacity-90 transition-opacity",
    completedTag: "text-sm text-orange-700 font-medium",
    progressLabel: "text-xs uppercase tracking-[0.2em] text-orange-600 font-medium",
    progressValue: "text-3xl font-light text-gray-900 tabular-nums",
    progressBar: "h-2 bg-orange-100",
    sectionTitle: "text-lg font-medium text-gray-900",
    sectionLabel: "text-xs uppercase tracking-[0.2em] text-orange-600 font-medium",
    subSectionLabel: "text-xs text-orange-500 font-medium",
    lessonRow: "w-full text-start px-4 py-2.5 text-sm flex items-center gap-3 rounded-xl hover:bg-orange-50/50 text-gray-700 transition-colors",
    lessonRowActive: "w-full text-start px-4 py-2.5 text-sm flex items-center gap-3 rounded-xl bg-orange-50 text-orange-900 font-medium shadow-sm transition-colors",
  };
}
